import {
  Dimensions,
  StyleSheet,
  Image,
  ScrollView,
  ActivityIndicator,
  Platform,
  View,
} from "react-native";
import React, { useContext, useEffect, useState } from "react";
import PageHeader from "../../components/PageHeader";
import Colors from "../../constants/Colors";
import { Text } from "../../components/Themed";
import { useForm } from "react-hook-form";
import InputText from "../../components/InputText";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import { Feather } from "@expo/vector-icons";
import InputSearch from "../../components/InputSearch";
import InputFooter from "../../components/InputFooter";
import useAutocomplete from "../../hooks/useAutocomplete";
import GlobalContext from "../../context/Global";
import { useMutation, useQueryClient } from "react-query";
import useCreateOpenHouse, {
  InputCreateOpenHouse,
} from "../../hooks/useCreateOpenHouse";
import InputDropdown from "../../components/InputDropdown";
import useSearchListings from "../../hooks/useSearchListings";
import uuid from "react-native-uuid";
import { useGetTags } from "../../hooks/useGetTags";
import { useNavigation, useRoute } from "@react-navigation/native";
import Toast from "react-native-toast-message";
import useNetworkHealth from "../../hooks/useNetworkHealth";
import DateTimeSection from "../../components/DateTimeSection";
import Pressable from "../../components/Pressable";
import { RadioButton } from "react-native-paper";
import moment from "moment";
import usePeakTimeRecommendation from "../../hooks/usePeakTimeRecommendation";

const { width } = Dimensions.get("window");

interface CreateOpenHouseProps {}

const CreateOpenHouse: React.FC<CreateOpenHouseProps> = ({}) => {
  const { goBack } = useNavigation();
  const { params } = useRoute();
  const network = useNetworkHealth();
  const queryClient = useQueryClient();
  const { user, isTablet, defaultForms } = useContext(GlobalContext);
  const [selectedListing, setSelectedListing] = useState<any>(null);
  const [selectedTags, setSelectedTags] = useState<any>([]);
  const [saving, setSaving] = useState(false);
  const [formValue, setFormValue] = useState(0);
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    reset,
    watch,
    setError,
    clearErrors,
  } = useForm();

  const _import = watch("import");
  const _tag = watch("tag");
  const _address = watch("address");
  const city = watch("city");
  const state = watch("state");
  const postalCode = watch("postal_code");
  const country = watch("country");
  const date = watch("date");

  const _removeTag = (tag: any) => {
    setSelectedTags((prevTags) => {
      return prevTags.filter((prevTag: any) => prevTag.label !== tag.label);
    });
  };

  const startTime = watch("startTime");
  const endTime = watch("endTime");
  const peakTimeRecommendation = usePeakTimeRecommendation({
    streetAddress: _address,
    city,
    state,
    postalCode,
    country,
    date,
    isOffline: network.isOffline,
  });

  React.useEffect(() => {
    startTime && startTime.setSeconds(0, 0);
    endTime && endTime.setSeconds(0, 0);

    if (startTime && endTime < startTime) {
      setError("endTime", {
        type: "manual",
        message: "End time must be after start time",
      });
    } else {
      clearErrors("endTime");
    }
  }, [startTime, endTime]);

  useEffect(() => {
    setValue("country", {
      label: "United States",
      value: "US",
    });
  }, []);

  useEffect(() => {
    if (_import?.value?.fieldValues || _import === "") {
      setValue("address", _import?.value?.fieldValues.address);
      setValue("city", _import?.value?.fieldValues.city);
      setValue("state", _import?.value?.fieldValues.state);
      setValue("postal_code", _import?.value?.fieldValues.postalCode);
      setTimeout(() => {
        refetch();
      }, 10);
      if (_import?.value?.fieldValues.address) {
        setSelectedTags([
          { label: "Open House" },
          { label: "openhouseapp" },
          { label: _import?.value?.fieldValues.address },
        ]);
      }
    } else {
      setSelectedListing(null);
    }
  }, [_import]);

  const _onSave = async (data: any) => {
    setSaving(true);
    if (Platform.OS === "web") {
      data.startTime = new Date(data.startTime);
      data.endTime = new Date(data.endTime);
    }
    const startDate = moment(data?.date).format("YYYY-MM-DD");
    const endDate = moment(data?.date).format("YYYY-MM-DD");
    if (!user?.openHouseUuid) return;
    _createOpenHouseMutation.mutate({
      openHouse: {
        uuid: uuid.v4()?.toString(),
        organization_uuid: user?.organizatonUuid,
        host_user_uuid: user?.openHouseUuid,
        created_by_user_uuid: user?.openHouseUuid,
        property_id: selectedListing?.blossorId || null,
        form_uuid: defaultForms[formValue]?.uuid,
        instructions: data.instructions,
        start_date: startDate,
        start_time: data?.startTime?.toLocaleTimeString("en-US", {
          hour12: false,
        }),
        end_date: endDate,
        end_time: data?.endTime?.toLocaleTimeString("en-US", {
          hour12: false,
        }),
        is_pending: false,
        manual_attendee_count: 0,
        is_active: false,
        active_at: null,
      },
      tags: selectedTags.map((tag: any) => tag.label),
      propertyDetails: {
        street_address: data.address,
        city: data.city,
        state: data.state,
        postal_code: data.postal_code,
        country: data.country.value,
      },
      pingTime: network.isOffline ? null : network.latencyMs ?? null,
    });
  };

  const _createOpenHouseMutation = useMutation(
    async (data: InputCreateOpenHouse) => {
      const res = await useCreateOpenHouse(data);
      return res;
    },
    {
      onSuccess: async () => {
        reset();
        Toast.show({
          type: "success",
          text1: "Open House Created",
          text2: "The open house has been created successfully.",
        });
        setTimeout(async () => {
          queryClient.invalidateQueries(["openHouses"]);
          queryClient.invalidateQueries(["dashboardStats"]);
          queryClient.fetchInfiniteQuery("openHouses");
          queryClient.fetchQuery("dashboardStats");
          goBack();
          setSaving(false);
        }, 800);
      },
    },
    {
      onError: () => {
        Toast.show({
          type: "error",
          text1: "Error",
          text2: "There was an error creating the open house.",
        });
      },
    }
  );

  const PageTitle = (props: any) => (
    <Text
      size={20}
      lineHeight={28}
      weight="SemiBold"
      lightColor={Colors.nuetral_grey_700}
    >
      {props.children}
    </Text>
  );

  const { data: autocompleteData, isLoading } = useAutocomplete(
    _import || "",
    user?.mlsIds,
    ["Active"],
    ["address", "ln", "city", "state", "postalCode"],
    [{ suggestType: "fullAddress" }],
    false
  );

  const formatNumber = (num: number) => {
    if (!num) return 0;
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const { data: listings, refetch } = useSearchListings({
    mlsIds: user?.mlsIds?.map(Number),
    statuses: [1],
    streetAddress: _import?.value?.fieldValues.address,
    city: _import?.value?.fieldValues.city,
    postalCode: _import?.value?.fieldValues.postalCode,
    stateAbbreviation: _import?.value?.fieldValues.state,
    limit: 1,
    propertyTypes: [],
  });

  useEffect(() => {
    if (listings?.listings?.length === 1) {
      setSelectedListing(listings?.listings[0] || {});
    }
  }, [listings]);

  const { data: tags } = useGetTags();

  const tagOptions = (searchTerm: string) => {
    if (!tags) return [];
    let options = tags?.map((tag: any) => {
      return {
        label: tag?.name,
        value: tag,
      };
    });
    if (searchTerm) {
      options = options?.filter((tag: any) =>
        tag?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    } else {
      options = [];
    }

    return options || [];
  };

  const defaultStartTime = new Date();
  defaultStartTime.setHours(9, 30, 0, 0);

  const defaultEndTime = new Date();
  defaultEndTime.setHours(11, 30, 0, 0);

  return (
    <View style={Platform.OS === "web" ? styles.webBody : styles.body}>
      <PageHeader
        contentAware={false}
        leftComponent={<PageTitle>Create Open House</PageTitle>}
        horizontalPadding={24}
      />
      {saving ? (
        <View style={styles.activityContainer}>
          <ActivityIndicator size="large" color={Colors.primary} />
        </View>
      ) : (
        <KeyboardAwareScrollView
          keyboardShouldPersistTaps="handled"
          extraScrollHeight={80}
          style={styles.flex}
        >
          <View style={styles.container}>
            {user?.mlsIds ? (
              <>
                {network.isOnline && (
                  <Text style={styles.instructions}>
                    Import a listing or fill out the information below.
                  </Text>
                )}
                {network.isOnline && (
                  <InputSearch
                    control={control}
                    label="Import Listing Details"
                    name="import"
                    errors={errors}
                    isLoading={isLoading}
                    blossorId={params?.blossorId}
                    options={
                      autocompleteData?.map((item) => {
                        return {
                          label: item.text,
                          value: item,
                        };
                      }) || []
                    }
                    onClear={() => {
                      setSelectedListing(null);
                      setValue("address", "");
                      setValue("city", "");
                      setValue("state", "");
                      setValue("postal_code", "");
                      setSelectedTags([
                        {
                          label: "Open House",
                        },
                        {
                          label: "openhouseapp",
                        },
                      ]);
                    }}
                    placeholder="Search for a listing address to auto-populate"
                  />
                )}
                {selectedListing !== null ? (
                  <View style={styles.propertyCard}>
                    {selectedListing?.photos?.length > 0 && (
                      <Image
                        style={[
                          styles.propertyImage,
                          {
                            width: isTablet ? 140 : 100,
                            height: isTablet ? 100 : 80,
                          },
                        ]}
                        source={{ uri: selectedListing?.photos[0]?.url }}
                      />
                    )}
                    <View style={styles.containerRow}>
                      <Text
                        size={isTablet ? 28 : 19}
                        lineHeight={isTablet ? 34 : 21}
                        weight="SemiBold"
                        style={styles.propertyPrice}
                      >
                        {`$${formatNumber(selectedListing?.price)}`}
                      </Text>

                      <View style={styles.propertyDetails}>
                        <View style={styles.propertyDetail}>
                          <Text
                            size={isTablet ? 18 : 15}
                            lineHeight={isTablet ? 20 : 18}
                            style={styles.detailText}
                          >
                            {selectedListing?.bedrooms || "--"}
                          </Text>
                          <Text
                            size={isTablet ? 18 : 15}
                            lineHeight={isTablet ? 20 : 18}
                            style={styles.detailText}
                          >
                            bed
                          </Text>
                          <Text
                            size={isTablet ? 18 : 15}
                            lineHeight={isTablet ? 20 : 18}
                            style={styles.detailText}
                          >
                            |
                          </Text>
                        </View>
                        <View style={styles.propertyDetail}>
                          <Text
                            size={isTablet ? 18 : 15}
                            lineHeight={isTablet ? 20 : 18}
                            style={styles.detailText}
                          >
                            {selectedListing?.bathsTotalDecimal || "--"}
                          </Text>
                          <Text
                            size={isTablet ? 18 : 15}
                            lineHeight={isTablet ? 20 : 18}
                            style={styles.detailText}
                          >
                            bath
                          </Text>
                          <Text
                            size={isTablet ? 18 : 15}
                            lineHeight={isTablet ? 20 : 18}
                            style={styles.detailText}
                          >
                            |
                          </Text>
                        </View>
                        <View style={styles.propertyDetail}>
                          <Text
                            size={isTablet ? 18 : 15}
                            lineHeight={isTablet ? 20 : 18}
                            style={styles.detailText}
                          >
                            {formatNumber(selectedListing?.squareFeet) || "--"}
                          </Text>
                          <Text
                            size={isTablet ? 18 : 15}
                            lineHeight={isTablet ? 20 : 18}
                            style={styles.detailText}
                          >
                            SqFt
                          </Text>
                        </View>
                      </View>
                      <Text
                        numberOfLines={1}
                        size={isTablet ? 18 : 15}
                        lineHeight={isTablet ? 20 : 18}
                        style={styles.propertyAddress}
                      >
                        {`${selectedListing?.streetAddress} ${selectedListing?.city}, ${selectedListing?.state} ${selectedListing?.postalCode}`}
                      </Text>
                    </View>
                  </View>
                ) : null}
              </>
            ) : null}
            <>
              {network.isOnline && <View style={styles.line} />}
              <View style={{ width: "100%" }}>
                <InputText
                  control={control}
                  label="Address"
                  name="address"
                  errors={errors}
                  placeholder="Address"
                  required
                  onBlurInput={(v) => {
                    if (v !== "" && v !== null && v !== undefined) {
                      setSelectedTags([
                        { label: "Open House" },
                        { label: "openhouseapp" },
                        { label: v },
                      ]);
                    }
                  }}
                  disabled={selectedListing !== null}
                  testingID="address_input"
                />
              </View>
              <View style={{ width: "100%" }}>
                <InputText
                  control={control}
                  label="City"
                  name="city"
                  errors={errors}
                  placeholder="City"
                  required
                  disabled={selectedListing !== null}
                  testingID="city_input"
                />
              </View>
              <View style={styles.stateRow}>
                <View
                  style={{
                    width: "48%",
                  }}
                >
                  <InputText
                    control={control}
                    label="State"
                    name="state"
                    errors={errors}
                    placeholder="State"
                    required
                    disabled={selectedListing !== null}
                    testingID="state_input"
                  />
                </View>
                <View style={styles.space} />
                <View
                  style={{
                    width: "48%",
                  }}
                >
                  <InputText
                    control={control}
                    label="Zip / Postal Code"
                    name="postal_code"
                    errors={errors}
                    placeholder="Zip / Postal Code"
                    required
                    disabled={selectedListing !== null}
                    testingID="postal_code_input"
                  />
                </View>
                {isTablet && (
                  <>
                    <View style={styles.space} />
                    <InputDropdown
                      control={control}
                      label="Country"
                      name="country"
                      errors={errors}
                      required
                      options={[
                        {
                          label: "United States",
                          value: "US",
                        },
                        {
                          label: "Canada",
                          value: "CA",
                        },
                      ]}
                      testingID="country_dropdown"
                    />
                  </>
                )}
              </View>
              {!isTablet && (
                <View style={{ zIndex: 101, width: "100%" }}>
                  <InputDropdown
                    control={control}
                    label="Country"
                    name="country"
                    errors={errors}
                    required
                    options={[
                      {
                        label: "United States",
                        value: "US",
                      },
                      {
                        label: "Canada",
                        value: "CA",
                      },
                    ]}
                    testingID="country_dropdown"
                  />
                </View>
              )}
            </>
            <View style={[styles.line]} />
            <View
              style={{
                width: "100%",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                zIndex: 100,
              }}
            >
              <DateTimeSection
                control={control}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                startTime={startTime}
                endTime={endTime}
                defaultEndTime={defaultEndTime}
                defaultStartTime={defaultStartTime}
                defaultDate={new Date()}
                isTablet={isTablet}
                setError={setError}
                clearErrors={clearErrors}
                watch={watch}
                peakTimeRecommendation={peakTimeRecommendation}
              />
            </View>
            {/* <InputText
            control={control}
            label="Host"
            name="showingAgent"
            errors={errors}
            placeholder
            ="Host"
          /> */}
            <RadioButton.Group
              onValueChange={(newValue) => setFormValue(newValue)}
              value={formValue}
            >
              <Text style={styles.labelText}>Registration Form</Text>
              <View style={styles.labelRow}>
                <Pressable
                  style={[styles.formRow, { marginLeft: -8 }]}
                  onPress={() => setFormValue(0)}
                >
                  <RadioButton.Android
                    value={0}
                    color={Colors.primary_500}
                    uncheckedColor={Colors.nuetral_grey_400}
                  />
                  <Text>Full Registration</Text>
                </Pressable>
                <Pressable
                  style={[styles.formRow, { marginLeft: 40 }]}
                  onPress={() => setFormValue(1)}
                >
                  <RadioButton.Android
                    value={1}
                    color={Colors.primary_500}
                    uncheckedColor={Colors.nuetral_grey_400}
                  />
                  <Text>Light Registration</Text>
                </Pressable>
              </View>
            </RadioButton.Group>
            <InputSearch
              control={control}
              label="Tags"
              name="tag"
              errors={errors}
              isLoading={false}
              options={tagOptions(_tag)}
              onClear={() => {
                setValue("tag", "");
              }}
              onSelected={(option) => {
                setSelectedTags((prevTags) => {
                  const _tagIndex = prevTags.findIndex(
                    (tag: any) => tag.label === option.label
                  );
                  if (_tagIndex === -1) {
                    return [...prevTags, option];
                  } else {
                    return prevTags.filter(
                      (tag: any) => tag.label !== option.label
                    );
                  }
                });
              }}
              placeholder="Search for tags"
            />

            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={styles.tagRow}
              contentContainerStyle={styles.tagContainerRow}
            >
              {selectedTags?.map((tag) => (
                <Pressable
                  disabled={
                    tag?.label === "Open House" ||
                    tag?.label === "openhouseapp" ||
                    tag?.label === _import?.value?.fieldValues.address ||
                    tag?.label === _address
                  }
                  key={tag?.label}
                  hitSlop={styles.hit}
                  onPress={() => _removeTag(tag)}
                >
                  <View
                    style={[
                      styles.tag,
                      {
                        backgroundColor:
                          tag?.label === "Open House" ||
                          tag?.label === "openhouseapp" ||
                          tag?.label === _import?.value?.fieldValues.address ||
                          tag?.label === _address
                            ? Colors.nuetral_grey_600
                            : Colors.nuetral_grey_400,
                      },
                    ]}
                  >
                    {tag?.label === "Open House" ||
                    tag?.label === "openhouseapp" ||
                    tag?.label === _import?.value?.fieldValues.address ||
                    tag?.label === _address ? null : (
                      <Feather
                        name="x"
                        size={14}
                        color="black"
                        style={{ marginRight: 2, marginBottom: 1 }}
                      />
                    )}
                    <Text
                      size={14}
                      lineHeight={14}
                      weight="SemiBold"
                      style={[
                        styles.tagText,
                        {
                          color:
                            tag?.label === "Open House" ||
                            tag?.label === "openhouseapp" ||
                            tag?.label ===
                              _import?.value?.fieldValues.address ||
                            tag?.label === _address
                              ? "white"
                              : "black",
                        },
                      ]}
                    >
                      {tag?.label}
                    </Text>
                  </View>
                </Pressable>
              ))}
            </ScrollView>
            <InputText
              control={control}
              label="Notes"
              name="instructions"
              errors={errors}
              placeholder="Enter message here"
              multiline
            />
          </View>
        </KeyboardAwareScrollView>
      )}
      <InputFooter
        handleSubmit={() =>
          Object.keys(errors).length === 0 && handleSubmit(_onSave)()
        }
        disabled={Object.keys(errors).length > 0}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  body: {
    flex: 1,
    backgroundColor: Colors.base_colors_white,
  },
  webBody: {
    flex: 1,
    backgroundColor: Colors.base_colors_white,
    width: "50%",
    alignSelf: "center",
    marginTop: 60,
    marginBottom: 60,
  },
  container: {
    paddingHorizontal: 20,
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
  },
  flex: {
    flex: 1,
  },
  row: {
    marginTop: 5,
    marginLeft: 40,
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    marginBottom: 20,
  },
  activityContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  stateRow: {
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center",
    zIndex: 100,
  },
  labelText: {
    fontSize: 16,
    marginTop: 8,
    paddingBottom: 4,
  },
  minus: {
    marginTop: 39,
    marginHorizontal: 10,
  },
  hit: { top: 10, bottom: 20, left: 4, right: 4 },
  space: {
    width: 20,
  },
  instructions: {
    fontSize: 14,
    lineHeight: 20,
    color: Colors.nuetral_grey_700,
    marginBottom: 20,
  },
  labelRow: {
    flexDirection: "row",
    marginBottom: 8,
  },
  errorStar: {
    fontSize: 18,
    marginBottom: -2,
    color: "#d42121",
  },
  line: {
    height: 1,
    backgroundColor: Colors.nuetral_grey_400,
    marginTop: -10,
    marginBottom: 15,
    width: "100%",
  },
  propertyCard: {
    borderRadius: 10,
    padding: 10,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: Colors.nuetral_grey_400,
    flexDirection: "row",
  },
  propertyImage: {
    borderRadius: 4,
  },
  containerRow: {
    marginLeft: 10,
    flex: 1,
  },
  propertyPrice: {
    color: "black",
    marginTop: 10,
  },
  propertyAddress: {
    color: Colors.nuetral_grey_700,
    marginTop: 5,
  },
  propertyDetails: {
    flexDirection: "row",
    marginTop: 5,
    flex: 1,
  },
  propertyDetail: {
    flexDirection: "row",
    alignItems: "center",
  },
  detailText: {
    color: Colors.nuetral_grey_700,
    marginRight: 5,
  },
  tagRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: -16,
    marginBottom: 16,
  },
  tag: {
    padding: 5,
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 20,
    paddingHorizontal: 10,
    justifyContent: "center",
    marginHorizontal: 4,
    paddingBottom: 4,
  },
  tagContainerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  tagText: {
    color: "black",
    paddingVertical: 3,
    paddingBottom: 0,
  },
  formRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
});

export default CreateOpenHouse;
